import {
  type MutationFunction,
  type QueryFunction,
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  useQuery,
  type UseQueryOptions
} from '@tanstack/react-query';
import { queryClient } from '@r/utils/utils.ts';
import { type ReactElement, type ReactNode, useCallback } from 'react';
import { loginCheckPost, me } from '@r/api/generated/client.ts';
import type { LoginCheckPostBody, JWTTokenModel } from '@r/api/generated/client.schemas.ts';
import { emitLoginSuccess } from '@r/components/changelogs/changelog-login-signal.ts';
import i18n from '@r/lib/i18n.ts';
import { isOrvalServerError, isOrvalServiceUnavailableError } from '@r/api/orval-error-helper.ts';
import { triggerServerError } from '@r/lib/server-error-events.ts';
import {
  maintenanceCriticalQueryRetry,
  maintenanceCriticalRetryDelay
} from '@r/lib/maintenance-critical-query.ts';

const APP_ID: string = import.meta.env.VITE_APP_ID;

export async function getUserProfile() {
  try {
    return await me();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function loginWithEmailAndPassword(data: LoginCheckPostBody) {
  return await loginCheckPost(data, { headers: { 'App-Id': APP_ID } });
}
export interface ReactQueryAuthConfig<User, LoginCredentials> {
  userFn: QueryFunction<User, QueryKey>;
  loginFn: MutationFunction<User, LoginCredentials>;
  userKey?: QueryKey;
}

async function handleUserResponse(data: JWTTokenModel) {
  localStorage.setItem('token', data.token as string);
  localStorage.setItem('refreshToken', data.refreshToken as string);
  return await userFn();
}

async function userFn() {
  if (!localStorage.getItem('token')) {
    return null;
  }
  return await getUserProfile();
}

async function loginFn(data: LoginCheckPostBody) {
  const response = await loginWithEmailAndPassword(data);
  try {
    const user = await handleUserResponse(response);
    await i18n.reloadResources();
    emitLoginSuccess();
    return user;
  } catch {
    throw new Error('Internal Server Error');
  }
}

export function configureAuth<User, Error, LoginCredentials>(
  config: ReactQueryAuthConfig<User, LoginCredentials>
) {
  const { userFn, userKey = ['authenticated-user'], loginFn } = config;

  /** Session : retente /users/me en 500 ; échec final → maintenance dans app.tsx */
  const useUser = (
    options: Omit<UseQueryOptions<User, Error, User, QueryKey>, 'queryKey' | 'queryFn'> = {}
  ) =>
    useQuery({
      queryKey: userKey,
      queryFn: userFn,
      ...options,
      retry: options.retry ?? maintenanceCriticalQueryRetry,
      retryDelay: options.retryDelay ?? maintenanceCriticalRetryDelay
    });

  /** POST /auth : retente en 500 ; échec final → triggerServerError (page maintenance) */
  const useLogin = (
    options?: Omit<UseMutationOptions<User, Error, LoginCredentials>, 'mutationFn'>
  ) => {
    const setUser = useCallback(
      (data: User | null) => queryClient.setQueryData(userKey, data),
      [userKey]
    );

    return useMutation({
      mutationFn: loginFn,
      ...options,
      retry: options?.retry ?? maintenanceCriticalQueryRetry,
      retryDelay: options?.retryDelay ?? maintenanceCriticalRetryDelay,
      onSuccess: async (user, ...rest) => {
        setUser(user);
        await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
        await queryClient.invalidateQueries({ queryKey: ['availableRoutes'] });
        options?.onSuccess?.(user, ...rest);
      },
      onError: (error, ...rest) => {
        setUser(null);
        if (isOrvalServiceUnavailableError(error)) {
          triggerServerError('503');
        } else if (isOrvalServerError(error)) {
          triggerServerError('500');
        }
        options?.onError?.(error, ...rest);
      }
    });
  };

  const useLogout = () => {
    const setUser = useCallback((data: null) => queryClient.setQueryData(userKey, data), [userKey]);

    return async () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      await i18n.reloadResources();
      await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
      // Purge l'entrée plutôt que la forcer à `[]` : une entrée « success + [] »
      // ferait passer `routesLoaded` à true au re-login sans rechargement, et le
      // garde de route lèverait un notFound (404) avant l'arrivée des vraies routes.
      queryClient.removeQueries({ queryKey: ['availableRoutes'] });
      setUser(null);
    };
  };

  function AuthLoader({
    children,
    renderLoading
  }: {
    children: ReactNode;
    renderLoading: () => ReactElement;
  }) {
    const { isFetched } = useUser();

    if (!isFetched) {
      return renderLoading();
    }

    return <>{children}</>;
  }

  return {
    useUser,
    useLogin,
    useLogout,
    AuthLoader
  };
}

export const { useUser, useLogin, useLogout, AuthLoader } = configureAuth({
  userFn,
  loginFn
});
